import Highcharts from 'highcharts';

import {
  type JobPreview,
  type SiteSoh,
} from '@/api/fe-api/domain/entities/capacity-evaluation';
import { formatDate } from '@/utils/format/date';
import { formatPercentage } from '@/utils/format/number';

import Style from '../styles-charts';

export function getSohChartConfig({
  dataPoints,
  endOfLifeThreshold,
  currentCapacityTest,
  capacityTests,
  onCapacityTestClick,
}: {
  dataPoints: SiteSoh[];
  currentCapacityTest: JobPreview | undefined;
  capacityTests: JobPreview[];
  onCapacityTestClick: (jobId: string) => void;
  endOfLifeThreshold: number;
}): Highcharts.Options {
  return {
    chart: {
      type: 'spline',
      style: Style.chart,
    },
    title: {
      text: 'SOH vs Completed Capacity Tests',
      useHTML: true,
      style: Style.chartTitle,
    },
    xAxis: {
      type: 'datetime',
      title: {
        text: 'Date',
        style: Style.axisTitle,
      },
      lineColor: Style.chartColors.gridLine,
      tickColor: Style.chartColors.gridLine,
      labels: Style.axisLabel,
    },
    yAxis: {
      title: { text: 'SOH-C', style: Style.axisTitle },
      labels: {
        ...Style.axisLabel,
        format: '{value}%',
      },
      min: 70,
      max: 110,
      gridLineColor: Style.chartColors.gridLine,
      plotBands: [
        {
          from: 0,
          to: endOfLifeThreshold,
          color: 'rgba(240, 240, 240, 0.5)',
          zIndex: 1,
          label: {
            text: 'End of life',
            align: 'left',
            verticalAlign: 'middle',
            style: {
              color: '#999999',
              fontStyle: 'italic',
            },
          },
        },
      ],
    },
    tooltip: {
      backgroundColor: 'rgba(255, 255, 255, 0.9)',
      borderWidth: 1,
      borderColor: Style.chartColors.gridLine,
      shadow: false,
      style: {
        padding: '10px',
      },
      formatter: function () {
        if (this.series.name === 'SOH-C') {
          return `<b>${Highcharts.dateFormat('%B %Y', this.x as number)}</b><br/>SOH-C: ${this.y}%`;
        }

        if (this.series.name === 'Capacity Tests') {
          return `<b>Capacity Test</b><br/>${Highcharts.dateFormat('%Y-%m-%d', this.x as number)} (${formatPercentage((this.y as number) / 100)})`;
        }

        return '';
      },
    },
    legend: {
      itemStyle: {
        color: Style.chartColors.darkGray,
        fontWeight: 'normal',
      },
    },
    plotOptions: {
      spline: {
        lineWidth: 2,
      },
      scatter: {
        cursor: 'pointer',
        point: {
          events: {
            click: function () {
              if ('jobId' in this) onCapacityTestClick(this.jobId as string);
            },
          },
        },
      },
      series: {
        states: {
          hover: {
            lineWidth: 3,
          },
        },
      },
    },
    series: [
      {
        name: 'SOH-C',
        type: 'spline',
        color: Style.chartColors.gray,
        data: dataPoints.map(getSohXY),
        marker: {
          enabled: false,
        },
      },
      {
        name: 'Capacity Tests',
        type: 'scatter',
        color: Style.chartColors.primary,
        data: capacityTests.map((test) =>
          getCapacityTestXY(test, test.id === currentCapacityTest?.id),
        ),
        marker: {
          enabled: true,
          symbol: 'triangle',
          radius: 8,
          fillColor: '#ffffff',
          lineWidth: 2,
          lineColor: Style.chartColors.primary,
        },
      },
    ],
    annotations: currentCapacityTest
      ? [createAnnotation(currentCapacityTest)]
      : [],
    credits: { enabled: false },
    responsive: {
      rules: [
        {
          condition: {
            maxWidth: 500,
          },
          chartOptions: {
            legend: {
              align: 'center',
              verticalAlign: 'bottom',
              layout: 'horizontal',
            },
          },
        },
      ],
    },
  };
}

function createAnnotation(
  capacityTest: JobPreview,
): Highcharts.AnnotationsOptions {
  return {
    labels: [
      {
        point: {
          xAxis: 0,
          yAxis: 0,
          ...getCapacityTestXY(capacityTest),
        },
        text: `<b>Selected Capacity Test</b><br>${formatDate(capacityTest.dischargeEndTime)} (${formatPercentage(capacityTest.sohc)})`,
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        borderColor: Style.chartColors.gridLine,
        borderWidth: 1,
        padding: 8,
        borderRadius: 5,
        style: {
          fontSize: '12px',
        },
        y: -15,
      },
    ],
    zIndex: 6,
  };
}

function getSohXY(data: SiteSoh) {
  return {
    x: Date.UTC(data.year, data.month, 1),
    y: data.avgSohC * 100,
  };
}

function getCapacityTestXY(data: JobPreview, isCurrent?: boolean) {
  return {
    x: data.dischargeEndTime.getTime(),
    y: data.sohc * 100,
    jobId: data.id,
    marker: isCurrent
      ? {
          fillColor: Style.chartColors.primary,
        }
      : undefined,
  };
}
